import { useEffect, useMemo, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useEventSpaces } from "@circle-react/components/Events/hooks/useEventSpaces";
import { LOCATION_TYPES } from "@circle-react/components/EventsV3/Modal/utils";
import { usePunditUserContext } from "@circle-react/contexts";
import {
  ROOM_ACCESS_TYPES,
  ROOM_TYPES,
  ROOM_VIEW_TYPES,
} from "@circle-react/helpers/liveStreamRoomHelpers";
import type { Event } from "@circle-react/types/Event";
import { isLiveLocation } from "../../../utils";

export const useLiveValuesWatch = (event: Event) => {
  const { currentCommunity } = usePunditUserContext();
  const { setValue, getValues } = useFormContext();
  const accessTypeName =
    "event_setting_attributes.live_stream_room_setting_attributes.access_type";
  const prevLocationTypeRef = useRef(
    event?.event_setting_attributes?.location_type,
  );
  const locationType = getValues().event_setting_attributes?.location_type;
  const { spaces } = useEventSpaces();
  const spaceId = useWatch({ name: "space_id" });
  const space = spaces?.find(space => space.id === spaceId) || null;
  const isPrivateSpace = ["private", "secret"].includes(
    space?.visibility ?? "",
  );
  const accessType = isPrivateSpace
    ? ROOM_ACCESS_TYPES.SECRET
    : ROOM_ACCESS_TYPES.OPEN;
  const currentAccessType = useWatch({
    name: accessTypeName,
  });

  const liveStreamRoomSettingAttributes = useMemo(() => {
    const defaultSettings = {
      access_type: accessType,
      view_type: ROOM_VIEW_TYPES.GRID,
      recording_enabled: currentCommunity?.live_stream_recording_enabled,
      auto_post_recording_enabled: false,
      room_type: ROOM_TYPES.STREAM,
    };

    const existingSettings =
      event?.event_setting_attributes?.live_stream_room_setting_attributes;

    return existingSettings
      ? { ...defaultSettings, ...existingSettings }
      : defaultSettings;
  }, [currentCommunity, event, accessType]);

  useEffect(() => {
    // Setting live_stream_room_setting_attributes only when locationType changes
    // and it's either a live room or live stream
    if (
      isLiveLocation(locationType) &&
      prevLocationTypeRef.current !== locationType
    ) {
      Object.entries(liveStreamRoomSettingAttributes).forEach(
        ([name, value]) => {
          setValue(
            `event_setting_attributes.live_stream_room_setting_attributes.${name}`,
            value,
            { shouldDirty: true, shouldTouch: true },
          );
        },
      );
      prevLocationTypeRef.current = locationType;
    }
    // When switching to room, if current access type is PUBLIC_STREAM
    // (which is not available for room), set it to the first available option (SECRET)
    if (
      locationType === LOCATION_TYPES.LIVE_ROOM &&
      currentAccessType === ROOM_ACCESS_TYPES.PUBLIC_STREAM
    ) {
      setValue(accessTypeName, ROOM_ACCESS_TYPES.SECRET, {
        shouldDirty: true,
      });
    }
  }, [locationType, liveStreamRoomSettingAttributes, setValue]);

  return { space };
};
